import { useState } from "react";
import { Play } from "lucide-react";
import { useUserSponsoredAd } from "@/hooks/useUserSponsoredAd";
import { extractYouTubeVideoId } from "@/lib/youtube";
import { SponsoredVideoModal } from "@/components/SponsoredVideoModal";
import { cn } from "@/lib/utils";

interface UserSponsorBadgeProps {
  userId?: string | null | undefined;
  userName?: string | null;
  className?: string;
  /** pill = small tag, cta = full-width button, overlay = big play button over a photo */
  variant?: "pill" | "cta" | "overlay";
}

/**
 * Pink pill surfacing a sponsored video attributed to a user profile.
 * Renders nothing when no active/public ad is linked to this user.
 */
export const UserSponsorBadge = ({ userId, userName, className, variant = "pill" }: UserSponsorBadgeProps) => {
  const [open, setOpen] = useState(false);
  const { ad } = useUserSponsoredAd(userId);
  if (!ad) return null;

  const videoId = extractYouTubeVideoId(ad.youtube_video_id);
  if (!videoId) return null;

  const label =
    ad.title_above?.trim() ||
    (userName ? `Watch ${userName}'s story` : "Watch their story");

  const openVideo = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setOpen(true);
  };

  const modal = (
    <SponsoredVideoModal open={open} onOpenChange={setOpen} videoId={videoId} title={ad.title} adId={ad.id} />
  );

  if (variant === "overlay") {
    return (
      <>
        <button
          type="button"
          onClick={openVideo}
          aria-label={label}
          className={cn(
            "absolute inset-0 z-10 flex items-center justify-center bg-foreground/10 transition-colors hover:bg-foreground/20 active:bg-foreground/30",
            className
          )}
        >
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg ring-4 ring-background/60 transition-transform group-hover:scale-110">
            <Play className="h-6 w-6 translate-x-0.5" fill="currentColor" />
          </span>
        </button>
        {modal}
      </>
    );
  }

  if (variant === "cta") {
    return (
      <>
        <button
          type="button"
          onClick={openVideo}
          aria-label={label}
          className={cn(
            "flex w-full min-h-[44px] items-center justify-center gap-2 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground shadow-sm transition-all hover:bg-primary/90 active:scale-[0.98]",
            className
          )}
        >
          <Play className="h-4 w-4" fill="currentColor" />
          <span className="truncate">{label}</span>
        </button>
        {modal}
      </>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setOpen(true);
        }}
        className={cn(
          "inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-background px-2.5 py-1 text-xs font-semibold text-primary shadow-sm transition-colors hover:bg-light-fuschia",
          className
        )}
        aria-label={label}
      >
        <Play className="h-3 w-3" fill="currentColor" />
        {label}
      </button>

      <SponsoredVideoModal
        open={open}
        onOpenChange={setOpen}
        videoId={videoId}
        title={ad.title}
        adId={ad.id}
      />
    </>
  );
};
